import React, { useRef, useState } from 'react';
import { Upload, X, Loader2, Image as ImageIcon } from 'lucide-react';

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function compressImage(file, maxDim = 900, quality = 0.78) {
  const dataUrl = await fileToDataUrl(file);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      if (width > height && width > maxDim) {
        height = (height * maxDim) / width;
        width = maxDim;
      } else if (height > maxDim) {
        width = (width * maxDim) / height;
        height = maxDim;
      }
      canvas.width = Math.round(width);
      canvas.height = Math.round(height);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      // Selalu JPEG untuk hasil lebih kecil (kecuali perlu transparansi PNG kecil)
      const preferPng = file.type === 'image/png' && file.size < 200 * 1024;
      resolve(canvas.toDataURL(preferPng ? 'image/png' : 'image/jpeg', quality));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

/** Upload gambar dari perangkat saja (tanpa tempel URL) */
export default function LocalImageUpload({
  label = 'Gambar Produk',
  value = '',
  onChange,
  maxDim = 1000,
  required = false,
}) {
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('File harus berupa gambar (JPG/PNG/WebP)');
      return;
    }
    setLoading(true);
    try {
      const compressed = await compressImage(file, maxDim, 0.78);
      onChange(compressed);
    } finally {
      setLoading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
        {label}{required ? ' *' : ''}
      </label>
      {value ? (
        <div className="relative border border-gray-200 rounded-xl p-3 bg-gray-50">
          <img
            src={value}
            alt={label}
            className="mx-auto h-36 max-w-full object-contain rounded-lg bg-white"
          />
          <div className="flex gap-2 mt-3">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="flex-1 py-2 text-xs font-semibold text-japo-green border border-japo-green rounded-lg hover:bg-rose-50"
            >
              Ganti Gambar
            </button>
            <button
              type="button"
              onClick={() => onChange('')}
              className="py-2 px-3 text-xs font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
              aria-label="Hapus"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={loading}
          className="w-full border-2 border-dashed border-gray-200 rounded-xl p-8 text-center text-sm text-gray-500 hover:border-japo-green hover:bg-rose-50/20 transition-colors flex flex-col items-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="w-6 h-6 text-japo-green animate-spin" />
              <span>Memproses gambar...</span>
            </>
          ) : (
            <>
              <div className="w-11 h-11 rounded-full bg-japo-green/10 flex items-center justify-center">
                <Upload className="w-5 h-5 text-japo-green" />
              </div>
              <span className="font-semibold text-gray-700">Upload dari perangkat</span>
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <ImageIcon className="w-3.5 h-3.5" /> JPG, PNG, WebP (max ~2MB)
              </span>
            </>
          )}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFile}
        className="hidden"
        required={required && !value}
      />
    </div>
  );
}
